import { Grid, LinearProgress, Typography } from "@material-ui/core";
import { Cancel, Save } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { routesPaths } from "../../../Routes";
import { useGetMeAsExpertQuery, useUpdateMeAsExpertMutation } from "../../../api/rtk/MeApi";
import { ValidationFields } from "../../../data/constants";
import { ExpertMeViewModel } from "../../../model/ExpertMeViewModel";
import { AddressFields } from "../../common/AddressFields";
import CustomButton from "../../common/buttons/CustomButton";
import RightButtonArea from "../../common/buttons/RightButtonArea";
import CustomDraggableDialog from "../../common/customDialog/CustomDraggableDialog";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { RequiredExpert, initialExpertMe, initialRequiredFields, initialValidationFields } from "../ExpertConstants";
import ExpertPersonalFields from "./ExpertPersonalFields";

const MyExpertProfile = () => {
  const history = useHistory();

  const [emailChanged, setEmailChanged] = useState(false);
  const [isEmailChangeDialogOpened, setIsEmailChangeDialogOpened] = useState(false);
  const [validationStatus, setValidationStatus] = useState(initialValidationFields);
  const [profile, setProfile] = useState<ExpertMeViewModel>(initialExpertMe);
  const [requiredFieldStatus, setRequiredFieldStatus] = useState(initialRequiredFields());
  const { data: currentExpert, isFetching: isCurrenExpertFetching } = useGetMeAsExpertQuery();
  const [updateMeAsExpert, { isLoading: isUpdateMeAsExpertLoading }] = useUpdateMeAsExpertMutation();

  useEffect(() => {
    if (currentExpert === undefined) {
      return;
    }

    setProfile(currentExpert);
  }, [currentExpert]);

  if (isCurrenExpertFetching || isUpdateMeAsExpertLoading) {
    return <LinearProgress variant="query" />;
  }

  if (profile === undefined) {
    return <></>;
  }

  const handleSave = () => {
    if (emailChanged) {
      setIsEmailChangeDialogOpened(true);
      return;
    }
    saveChanges();
  };

  const handleConfirmEmailChange = () => {
    setIsEmailChangeDialogOpened(false);
    saveChanges();
  };

  const saveChanges = () => {
    if (!allFieldsValid() || !validateRequiredFields()) {
      return;
    }

    updateMeAsExpert(profile)
      .unwrap()
      .then(() => history.push(routesPaths.root()));
  };

  const allFieldsValid = () =>
    validationStatus.isAhvValid &&
    validationStatus.isCiceroValid &&
    validationStatus.isEmailValid &&
    validationStatus.isMobileValid &&
    validationStatus.isPhoneValid;

  const validateRequiredFields = () => {
    const newStatus: RequiredExpert = {
      isFirstnameEmpty: !profile.firstName,
      isLastnameEmpty: !profile.lastName,
      isGenderEmpty: !profile.gender,
      isBirthDateEmpty: !profile.birthdate,
      isEmailEmpty: !profile.emailAddress,
      isStreetEmpty: !profile.street,
      isStreetNumberEmpty: !profile.streetNumber,
      isZipCodeEmpty: !profile.zipCode,
      isCityEmpty: !profile.city,
      isCountryEmpty: !profile.country,
      isLanguageEmpty: false,
      isAhvNoEmpty: !profile.ahvNumber,
      isMobileNoEmpty: !profile.mobileNumber,
      isBankAccountEmpty: !profile.bankAccount,
      hasEmptyRole: false,
      hasEmptyRoleArray: [],
    };

    const values = Object.values(newStatus);
    const hasMissingFields = values.some((value) => value === true);
    if (hasMissingFields) {
      setRequiredFieldStatus(newStatus);
    }
    return !hasMissingFields;
  };

  const handleFieldChange = (change: Partial<ExpertMeViewModel>) => {
    if (change.emailAddress) {
      setEmailChanged(true);
    }
    setProfile((prevProfile) => ({ ...prevProfile, ...change }));
  };

  const handleFieldValidation = (fieldName: keyof ValidationFields, isValid: boolean) => {
    if (validationStatus[fieldName] !== isValid) {
      setValidationStatus({ ...validationStatus, [fieldName]: isValid });
    }
  };

  return (
    <>
      <RightButtonArea>
        <CustomButton tooltip={<TranslatedMessage id="common.save" />} onClick={handleSave}>
          <Save />
        </CustomButton>
        <CustomButton
          tooltip={<TranslatedMessage id="common.cancel" />}
          onClick={() => history.push(routesPaths.root())}
        >
          <Cancel />
        </CustomButton>
      </RightButtonArea>
      <Grid container={true} spacing={5} direction="column">
        <Grid item={true}>
          <ExpertPersonalFields
            expert={profile}
            handleChange={handleFieldChange}
            requiredFields={requiredFieldStatus}
            handleEmailIsValid={(isValid: boolean) => handleFieldValidation("isEmailValid", isValid)}
            handleAhvIsValid={(isValid: boolean) => handleFieldValidation("isAhvValid", isValid)}
            handleCiceroIsValid={(isValid: boolean) => handleFieldValidation("isCiceroValid", isValid)}
            handlePhoneIsValid={(isValid: boolean) => handleFieldValidation("isPhoneValid", isValid)}
            handleMobileIsValid={(isValid: boolean) => handleFieldValidation("isMobileValid", isValid)}
          />
        </Grid>
        <Grid item={true}>
          <Typography variant="h6">
            <TranslatedMessage id="person.section.address" />
          </Typography>
          <Grid container={true} spacing={4}>
            <AddressFields
              fieldValues={profile}
              handleChange={handleFieldChange}
              fieldsValidation={requiredFieldStatus}
            />
          </Grid>
        </Grid>
        <CustomDraggableDialog
          title=""
          acceptText={<TranslatedMessage id="common.yes" />}
          cancelText={<TranslatedMessage id="common.no" />}
          content={<TranslatedMessage id="person.validation.confirmEmailChange" />}
          isOpen={isEmailChangeDialogOpened}
          onAccept={handleConfirmEmailChange}
          onCancel={() => setIsEmailChangeDialogOpened(false)}
        />
      </Grid>
    </>
  );
};

export default MyExpertProfile;
